import { useEffect, useRef, useState } from 'react';
import {
  initPostHog,
  identifyUser,
  setOrganizationContext,
  clearOrganizationContext,
  resetUser,
  startSessionRecording,
  stopPostHog,
} from '~/lib/posthog/client';
import { useConsent } from '~/core/consent/ConsentProvider';
import { useUserSession } from '~/core/hooks/use-user-session';
import { useCurrentOrganization } from '~/lib/organizations/hooks/use-current-organization';

const BOT_PATTERN =
  /bot|crawler|spider|crawling|headless|lighthouse|pingdom|pagespeed|prerender|phantom|selenium|webdriver/i;

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const userSession = useUserSession();
  const auth = userSession?.auth;
  const organization = useCurrentOrganization();
  const { choice, ready } = useConsent();
  const analyticsAllowed = ready && choice?.analytics === true;
  const consentGrantId = analyticsAllowed ? choice?.analyticsGrantId : null;
  const recordingStarted = useRef(false);
  const startedGrantId = useRef<string | null>(null);
  const [initializedGrantId, setInitializedGrantId] = useState<string | null>(
    null,
  );
  const initialized = !!consentGrantId && initializedGrantId === consentGrantId;

  // The SDK chunk and its network traffic are absent until explicit opt-in.
  useEffect(() => {
    // ConsentProvider has not read the saved choice on the first client render.
    if (!ready) return;
    if (!analyticsAllowed || !consentGrantId) {
      stopPostHog();
      startedGrantId.current = null;
      setInitializedGrantId(null);
      recordingStarted.current = false;
      return;
    }
    if (startedGrantId.current && startedGrantId.current !== consentGrantId) {
      // A different grant can mean another tab withdrew and regranted before
      // this tab observed the intermediate denial. Discard the old recording.
      stopPostHog();
      setInitializedGrantId(null);
      recordingStarted.current = false;
    }
    if (
      typeof navigator !== 'undefined' &&
      BOT_PATTERN.test(navigator.userAgent)
    )
      return;
    startedGrantId.current = consentGrantId;
    let active = true;
    void initPostHog()
      .then((started) => {
        // An older request must not stop analytics after consent is restored.
        if (active) setInitializedGrantId(started ? consentGrantId : null);
      })
      .catch(() => {
        if (active) setInitializedGrantId(null);
      });
    return () => {
      active = false;
    };
  }, [ready, analyticsAllowed, consentGrantId]);

  // Identify user when auth changes
  useEffect(() => {
    if (!initialized) return;

    if (!auth?.uid) {
      resetUser();
      recordingStarted.current = false;
      return;
    }

    identifyUser(auth.uid, {
      email: auth.email ?? undefined,
      name: auth.displayName ?? undefined,
      authProvider: auth.providerId ?? undefined,
      emailVerified: auth.emailVerified,
    });

    // Authenticated users: always record
    if (!recordingStarted.current) {
      startSessionRecording();
      recordingStarted.current = true;
    }
  }, [auth, initialized]);

  // Set organization context when org changes (super property on all events)
  useEffect(() => {
    if (!initialized) return;

    if (!organization) {
      clearOrganizationContext();
      return;
    }

    setOrganizationContext(organization.id, organization.name);
  }, [organization, initialized]);

  // Anonymous users: interaction-gated recording with bot detection
  useEffect(() => {
    if (!initialized) return;
    if (auth?.uid) return;
    if (typeof window === 'undefined') return;

    // Bot detection (enhanced)
    const isBot = BOT_PATTERN.test(navigator.userAgent) || !navigator.language;

    if (isBot) {
      stopPostHog();
      return;
    }

    let cleanedUp = false;

    const startRecordingOnInteraction = () => {
      if (cleanedUp || recordingStarted.current) return;
      startSessionRecording();
      recordingStarted.current = true;
      // Remove all listeners after starting
      window.removeEventListener('mousemove', startRecordingOnInteraction);
      window.removeEventListener('scroll', startRecordingOnInteraction);
      window.removeEventListener('click', startRecordingOnInteraction);
      window.removeEventListener('keydown', startRecordingOnInteraction);
    };

    window.addEventListener('mousemove', startRecordingOnInteraction);
    window.addEventListener('scroll', startRecordingOnInteraction);
    window.addEventListener('click', startRecordingOnInteraction);
    window.addEventListener('keydown', startRecordingOnInteraction);

    return () => {
      cleanedUp = true;
      window.removeEventListener('mousemove', startRecordingOnInteraction);
      window.removeEventListener('scroll', startRecordingOnInteraction);
      window.removeEventListener('click', startRecordingOnInteraction);
      window.removeEventListener('keydown', startRecordingOnInteraction);
    };
  }, [auth?.uid, initialized]);

  return <>{children}</>;
}
